import { useState, useContext, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MainSectionSection from "../../components/MainSectionSection";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";
import CalendarsConfiguration from "../../components/calendars/CalendarsConfiguration";

type TimeObject = {
  hour: string;
  minute: string;
  period: string;
};

type AppointmentConfigForm = {
  minimumAdvanceHoursRent: number;
  minimumAdvanceHoursSales: number;
  saturdayDayEnd: TimeObject;
  saturdayDayStart: TimeObject;
  sundayDayEnd: TimeObject;
  sundayDayStart: TimeObject;
  visitOnSaturdayAllowed: boolean;
  visitOnSundayAllowed: boolean;
  weekDayEnd: TimeObject;
  weekDayStart: TimeObject;
  holidays: string[];
  appointmentReminderEnabled: boolean;
  appointmentReminderHoursBefore: number;
};

/**
 * What the merchant sees beneath the switch. The template is submitted to Meta on save and
 * reminders stay dark until Meta approves it, so a merchant who turns this on and sees
 * nothing happen has to be able to find out why here rather than by contacting support.
 */
const reminderTemplateStatusCopy = (
  template: { status: string; statusReason?: string } | null | undefined
): { text: string; tone: "muted" | "ok" | "warn" | "error" } => {
  if (!template) return { text: "La plantilla se creara al guardar.", tone: "muted" };

  switch (template.status) {
    case "APPROVED":
      return { text: "Activo. Los recordatorios se estan enviando.", tone: "ok" };
    case "PENDING":
      return {
        text: "Pendiente de aprobacion de Meta. Los recordatorios empezaran automaticamente cuando se apruebe.",
        tone: "warn",
      };
    case "REJECTED":
    case "FAILED":
      return {
        text: template.statusReason
          ? `Rechazada por Meta: ${template.statusReason}`
          : "Rechazada por Meta.",
        tone: "error",
      };
    case "DELETED":
      return { text: "La plantilla fue eliminada. Se volvera a crear al guardar.", tone: "warn" };
    default:
      return { text: `Estado: ${template.status}`, tone: "muted" };
  }
};

const reminderStatusToneClass: Record<string, string> = {
  muted: "text-gray-600",
  ok: "text-green-700",
  warn: "text-yellow-700",
  error: "text-red-600",
};

const AppointmentConfigurationContainer = (): JSX.Element => {
  const { merchantId, selfConfigEnabled, calComIntegrationEnabled } = useContext(MerchantContext);

  const [formData, setFormData] = useState<AppointmentConfigForm>({
    minimumAdvanceHoursRent: 12,
    minimumAdvanceHoursSales: 12,
    saturdayDayEnd: { hour: "11", minute: "30", period: "AM" },
    saturdayDayStart: { hour: "9", minute: "00", period: "AM" },
    sundayDayEnd: { hour: "2", minute: "00", period: "PM" },
    sundayDayStart: { hour: "9", minute: "00", period: "AM" },
    visitOnSaturdayAllowed: true,
    visitOnSundayAllowed: false,
    weekDayEnd: { hour: "5", minute: "00", period: "PM" },
    weekDayStart: { hour: "8", minute: "30", period: "AM" },
    holidays: [],
    appointmentReminderEnabled: false,
    appointmentReminderHoursBefore: 12,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [newHolidayInput, setNewHolidayInput] = useState("");

  // Function to format time for display in prompt
  const formatTimeForPrompt = (timeObj: TimeObject) => {
    return `${timeObj.hour}:${timeObj.minute} ${timeObj.period}`;
  };

  // Time selector component
  const TimeSelector = ({
    timeValue,
    onChange,
    label,
  }: {
    timeValue: TimeObject;
    onChange: (value: TimeObject) => void;
    label: string;
  }) => {
    return (
      <div>
        <Label>{label}</Label>
        <div className="mt-1 flex flex-row items-center gap-2">
          <select
            value={timeValue.hour}
            onChange={(e) => onChange({ ...timeValue, hour: e.target.value })}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((hour) => (
              <option key={hour} value={hour.toString()}>
                {hour}
              </option>
            ))}
          </select>
          <span>:</span>
          <select
            value={timeValue.minute}
            onChange={(e) => onChange({ ...timeValue, minute: e.target.value })}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
              <option key={minute} value={minute.toString().padStart(2, "0")}>
                {minute.toString().padStart(2, "0")}
              </option>
            ))}
          </select>
          <select
            value={timeValue.period}
            onChange={(e) => onChange({ ...timeValue, period: e.target.value })}
            className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
    );
  };

  // TRPC queries
  const {
    data: appointmentConfigData,
    isLoading: isLoadingConfig,
    refetch,
  } = trpc.appointmentConfig.get.useQuery(
    { merchantId },
    {
      enabled: !!merchantId,
    }
  );

  const updateConfigMutation = trpc.appointmentConfig.update.useMutation({
    onSuccess: () => {
      toast.success("Configuracion guardada", {
        description: "Los horarios de atencion han sido actualizados exitosamente",
      });
      refetch();
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Hubo un error al guardar la configuracion",
      });
    },
  });

  // Function to parse time string from backend to TimeObject
  const parseTimeFromBackend = (timeStr: string): TimeObject => {
    if (!timeStr) return { hour: "8", minute: "30", period: "AM" };

    const formattedTime = timeStr.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());
    const timeMatch = formattedTime.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/);

    if (timeMatch) {
      return {
        hour: timeMatch[1],
        minute: timeMatch[2],
        period: timeMatch[3],
      };
    }

    return { hour: "8", minute: "30", period: "AM" };
  };

  const currentConfig = appointmentConfigData?.config;

  // Load current configuration when available
  useEffect(() => {
    if (currentConfig) {
      setFormData({
        minimumAdvanceHoursRent: currentConfig.minimumAdvanceHoursRent || 12,
        minimumAdvanceHoursSales: currentConfig.minimumAdvanceHoursSales || 12,
        saturdayDayEnd: parseTimeFromBackend(currentConfig.saturdayDayEnd || "11:30 AM"),
        saturdayDayStart: parseTimeFromBackend(currentConfig.saturdayDayStart || "9:00 AM"),
        sundayDayEnd: parseTimeFromBackend(currentConfig.sundayDayEnd || "2:00 PM"),
        sundayDayStart: parseTimeFromBackend(currentConfig.sundayDayStart || "9:00 AM"),
        visitOnSaturdayAllowed: currentConfig.visitOnSaturdayAllowed ?? true,
        visitOnSundayAllowed: currentConfig.visitOnSundayAllowed ?? false,
        weekDayEnd: parseTimeFromBackend(currentConfig.weekDayEnd || "5:00 PM"),
        weekDayStart: parseTimeFromBackend(currentConfig.weekDayStart || "8:30 AM"),
        holidays: currentConfig.holidays || [],
        appointmentReminderEnabled: currentConfig.appointmentReminderEnabled ?? false,
        appointmentReminderHoursBefore: currentConfig.appointmentReminderHoursBefore ?? 12,
      });
    }
  }, [currentConfig]);

  const handleInputChange = (field: keyof AppointmentConfigForm, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddHoliday = () => {
    const newHoliday = newHolidayInput.trim();
    if (!newHoliday) return;

    // Validate date format YYYY-MM-DD
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newHoliday)) {
      toast.error("Error de formato", {
        description: "El formato de fecha debe ser YYYY-MM-DD",
      });
      return;
    }

    // Check if date already exists
    if (formData.holidays.includes(newHoliday)) {
      toast.error("Fecha duplicada", {
        description: "Esta fecha ya esta en la lista de dias festivos",
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      holidays: [...prev.holidays, newHoliday].sort(),
    }));

    // Clear input
    setNewHolidayInput("");
  };

  const handleRemoveHoliday = (holidayToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      holidays: prev.holidays.filter((holiday) => holiday !== holidayToRemove),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAddHoliday();
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await updateConfigMutation.mutateAsync({
        merchantId,
        minimumAdvanceHoursRent: formData.minimumAdvanceHoursRent,
        minimumAdvanceHoursSales: formData.minimumAdvanceHoursSales,
        saturdayDayEnd: formatTimeForPrompt(formData.saturdayDayEnd),
        saturdayDayStart: formatTimeForPrompt(formData.saturdayDayStart),
        sundayDayEnd: formatTimeForPrompt(formData.sundayDayEnd),
        sundayDayStart: formatTimeForPrompt(formData.sundayDayStart),
        visitOnSaturdayAllowed: formData.visitOnSaturdayAllowed,
        visitOnSundayAllowed: formData.visitOnSundayAllowed,
        weekDayEnd: formatTimeForPrompt(formData.weekDayEnd),
        weekDayStart: formatTimeForPrompt(formData.weekDayStart),
        holidays: formData.holidays,
        appointmentReminderEnabled: formData.appointmentReminderEnabled,
        appointmentReminderHoursBefore: formData.appointmentReminderHoursBefore,
      });
    } catch (error) {
      console.error("Error updating appointment config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!merchantId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p>No se encontro el negocio.</p>
      </div>
    );
  }

  // Check if self-configuration is enabled
  if (!selfConfigEnabled) {
    return (
      <MainSectionSection gap="24px">
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <div className="flex flex-col gap-4">
            <p className="text-xl font-bold text-gray-700">
              Proximamente
            </p>
            <p className="text-lg text-gray-600" style={{ maxWidth: "500px", margin: "0 auto" }}>
              Podras autogestionar tu horario de atencion.
            </p>
            <p className="text-sm text-gray-500">
              Esta funcionalidad estara disponible proximamente para tu negocio.
            </p>
          </div>
        </div>
      </MainSectionSection>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  // Content for business hours configuration
  const BusinessHoursContent = (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="mb-2 text-xl font-bold">
            Horarios de Atencion
          </h2>
          <p className="text-gray-600">
            {calComIntegrationEnabled
              ? "Configura los horarios de atencion para los asesores que no tienen Cal.com. Si un asesor tiene Cal.com configurado, se usara su calendario."
              : "Configura los horarios de atencion y los tiempos minimos de anticipacion para las citas."}
          </p>
        </div>

          {/* Horarios dias laborales */}
          <div>
            <p className="mb-4 text-lg font-semibold">
              Dias Laborales (Lunes a Viernes)
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TimeSelector
                timeValue={formData.weekDayStart}
                onChange={(value) => handleInputChange("weekDayStart", value)}
                label="Horario de inicio"
              />
              <TimeSelector
                timeValue={formData.weekDayEnd}
                onChange={(value) => handleInputChange("weekDayEnd", value)}
                label="Horario de fin"
              />
            </div>
          </div>

          {/* Configuraciones de disponibilidad */}
          <div>
            <p className="mb-4 text-lg font-semibold">
              Disponibilidad de Visitas
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <Checkbox
                  checked={formData.visitOnSaturdayAllowed}
                  onCheckedChange={(checked) => handleInputChange("visitOnSaturdayAllowed", checked === true)}
                  className="mt-1 size-5"
                />
                <div>
                  <p className="font-medium">Visitas los sabados</p>
                  <p className="text-sm text-gray-600">
                    Permitir agendar citas los sabados
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Checkbox
                  checked={formData.visitOnSundayAllowed}
                  onCheckedChange={(checked) => handleInputChange("visitOnSundayAllowed", checked === true)}
                  className="mt-1 size-5"
                />
                <div>
                  <p className="font-medium">Visitas los domingos</p>
                  <p className="text-sm text-gray-600">
                    Permitir agendar citas los domingos
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Horarios sabado - Solo mostrar si visitOnSaturdayAllowed es true */}
          {formData.visitOnSaturdayAllowed && (
            <div>
              <p className="mb-4 text-lg font-semibold">
                Sabados
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TimeSelector
                  timeValue={formData.saturdayDayStart}
                  onChange={(value) => handleInputChange("saturdayDayStart", value)}
                  label="Horario de inicio"
                />
                <TimeSelector
                  timeValue={formData.saturdayDayEnd}
                  onChange={(value) => handleInputChange("saturdayDayEnd", value)}
                  label="Horario de fin"
                />
              </div>
            </div>
          )}

          {/* Horarios domingo - Solo mostrar si visitOnSundayAllowed es true */}
          {formData.visitOnSundayAllowed && (
            <div>
              <p className="mb-4 text-lg font-semibold">
                Domingos
              </p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TimeSelector
                  timeValue={formData.sundayDayStart}
                  onChange={(value) => handleInputChange("sundayDayStart", value)}
                  label="Horario de inicio"
                />
                <TimeSelector
                  timeValue={formData.sundayDayEnd}
                  onChange={(value) => handleInputChange("sundayDayEnd", value)}
                  label="Horario de fin"
                />
              </div>
            </div>
          )}

          {/* Tiempos minimos de anticipacion */}
          <div>
            <p className="mb-4 text-lg font-semibold">
              Tiempos Minimos de Anticipacion
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Horas minimas para arriendos</Label>
                <Input
                  type="number"
                  value={formData.minimumAdvanceHoursRent}
                  onChange={(e) =>
                    handleInputChange("minimumAdvanceHoursRent", Number(e.target.value))
                  }
                  min={0}
                  max={168}
                  className="mt-1"
                />
                <p className="mt-1 text-sm text-gray-600">
                  Horas habiles minimas para agendar citas de arriendo
                </p>
              </div>
              <div>
                <Label>Horas minimas para ventas</Label>
                <Input
                  type="number"
                  value={formData.minimumAdvanceHoursSales}
                  onChange={(e) =>
                    handleInputChange("minimumAdvanceHoursSales", Number(e.target.value))
                  }
                  min={0}
                  max={168}
                  className="mt-1"
                />
                <p className="mt-1 text-sm text-gray-600">
                  Horas habiles minimas para agendar citas de venta
                </p>
              </div>
            </div>
          </div>

          {/* Recordatorio de confirmacion */}
          <div className="border-t border-gray-200 pt-6">
            <p className="mb-2 text-lg font-semibold">
              Recordatorio de Confirmacion
            </p>
            <p className="mb-4 text-sm text-gray-600">
              Envia un WhatsApp al cliente antes de su cita para que confirme o cancele. Si
              responde, la cita y el lead se actualizan solos y se le avisa al asesor.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <Switch
                  checked={formData.appointmentReminderEnabled}
                  onCheckedChange={(checked) =>
                    handleInputChange("appointmentReminderEnabled", checked === true)
                  }
                  className="mt-1"
                />
                <div>
                  <p className="font-medium">Activar recordatorios</p>
                  <p className="text-sm text-gray-600">
                    Al guardar con esta opcion activa se crea la plantilla de WhatsApp y se
                    envia a Meta para aprobacion.
                  </p>
                </div>
              </div>

              <div className="md:max-w-xs">
                <Label>Horas de anticipacion</Label>
                <Input
                  type="number"
                  value={formData.appointmentReminderHoursBefore}
                  onChange={(e) =>
                    handleInputChange("appointmentReminderHoursBefore", Number(e.target.value))
                  }
                  min={1}
                  max={168}
                  disabled={!formData.appointmentReminderEnabled}
                  className="mt-1"
                />
                <p className="mt-1 text-sm text-gray-600">
                  Cuantas horas antes del inicio de la cita se envia el recordatorio. Si esa
                  hora cae fuera del horario de atencion configurado arriba, se envia al
                  abrir el siguiente horario habil.
                </p>
              </div>

              {formData.appointmentReminderEnabled && (
                <div className="rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium">Estado de la plantilla</p>
                  <p
                    className={`mt-1 text-sm ${
                      reminderStatusToneClass[
                        reminderTemplateStatusCopy(appointmentConfigData?.reminderTemplate).tone
                      ]
                    }`}
                  >
                    {reminderTemplateStatusCopy(appointmentConfigData?.reminderTemplate).text}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dias Festivos */}
          <div>
            <p className="mb-4 text-lg font-semibold">
              Dias Festivos
            </p>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Agregar fechas en las que no se permiten citas (formato: YYYY-MM-DD)
              </p>

              {/* Input para agregar nueva fecha */}
              <div className="relative">
                <Input
                  placeholder="2025-12-25"
                  value={newHolidayInput}
                  onChange={(e) => setNewHolidayInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pr-10"
                />
                <button
                  type="button"
                  aria-label="Agregar dia festivo"
                  onClick={handleAddHoliday}
                  disabled={!newHolidayInput.trim()}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                >
                  <Plus className="size-4" />
                </button>
              </div>

              {/* Lista de dias festivos */}
              {formData.holidays.length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-medium">
                    Dias festivos configurados:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.holidays.map((holiday) => (
                      <span
                        key={holiday}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-sm text-blue-800"
                      >
                        {holiday}
                        <button
                          type="button"
                          onClick={() => handleRemoveHoliday(holiday)}
                          className="rounded-full p-0.5 hover:bg-blue-200"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botones de accion */}
          <div className="flex flex-col-reverse gap-4 pt-4 md:flex-row md:justify-end">
            <Button
              variant="outline"
              className="min-h-11 w-full md:w-auto"
              onClick={() => {
                if (currentConfig) {
                  setFormData({
                    minimumAdvanceHoursRent: currentConfig.minimumAdvanceHoursRent || 12,
                    minimumAdvanceHoursSales: currentConfig.minimumAdvanceHoursSales || 12,
                    saturdayDayEnd: parseTimeFromBackend(
                      currentConfig.saturdayDayEnd || "11:30 AM"
                    ),
                    saturdayDayStart: parseTimeFromBackend(
                      currentConfig.saturdayDayStart || "9:00 AM"
                    ),
                    sundayDayEnd: parseTimeFromBackend(currentConfig.sundayDayEnd || "2:00 PM"),
                    sundayDayStart: parseTimeFromBackend(currentConfig.sundayDayStart || "9:00 AM"),
                    visitOnSaturdayAllowed: currentConfig.visitOnSaturdayAllowed ?? true,
                    visitOnSundayAllowed: currentConfig.visitOnSundayAllowed ?? false,
                    weekDayEnd: parseTimeFromBackend(currentConfig.weekDayEnd || "5:00 PM"),
                    weekDayStart: parseTimeFromBackend(currentConfig.weekDayStart || "8:30 AM"),
                    holidays: currentConfig.holidays || [],
                    appointmentReminderEnabled: currentConfig.appointmentReminderEnabled ?? false,
                    appointmentReminderHoursBefore:
                      currentConfig.appointmentReminderHoursBefore ?? 12,
                  });
                }
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading || updateConfigMutation.isLoading}
              className="min-h-11 w-full md:w-auto"
            >
              {(isLoading || updateConfigMutation.isLoading) && <Loader2 className="size-4 animate-spin" />}
              {(isLoading || updateConfigMutation.isLoading) ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </div>
        </div>
      </div>
  );

  // If Cal.com integration is enabled, show tabs
  if (calComIntegrationEnabled) {
    return (
      <MainSectionSection gap="24px">
        <Tabs defaultValue="horarios" className="flex min-h-full flex-col">
          <TabsList className="sticky top-0 z-10 flex w-full justify-center bg-white shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
            <TabsTrigger value="horarios" className="flex-1 text-center">
              Horarios
            </TabsTrigger>
            <TabsTrigger value="calendarios" className="flex-1 text-center">
              Calendarios
            </TabsTrigger>
          </TabsList>
          <TabsContent value="horarios" className="flex-1 py-4">
            {BusinessHoursContent}
          </TabsContent>
          <TabsContent value="calendarios" className="flex-1 py-4">
            <CalendarsConfiguration />
          </TabsContent>
        </Tabs>
      </MainSectionSection>
    );
  }

  // Without Cal.com integration, show only business hours
  return <MainSectionSection gap="24px">{BusinessHoursContent}</MainSectionSection>;
};

export default AppointmentConfigurationContainer;
